import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  InputAdornment,
  Alert,
  Container,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import CustomTable from "../../../../../../../components/custom-components/CustomTable";

const LocatorsPage = ({ locators, onLocatorsUpdate }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editedData, setEditedData] = useState({
    fieldName: "",
    primary: [],
    secondary: [],
  });

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Validate locators data structure
  const isValidLocatorsData = (locatorsData) => {
    if (!locatorsData || typeof locatorsData !== "object") {
      return false;
    }

    // Check if it's a string (error message)
    if (typeof locatorsData === "string") {
      return false;
    }

    // Check if it has the expected structure
    const entries = Object.entries(locatorsData);
    if (entries.length === 0) {
      return false;
    }

    // Validate at least one entry has primary and secondary arrays
    return entries.some(([key, value]) => {
      return (
        value &&
        typeof value === "object" &&
        Array.isArray(value.primary) &&
        Array.isArray(value.secondary)
      );
    });
  };

  // Function to transform API response to table data
  const transformApiResponseToTableData = (apiResponse) => {
    if (!isValidLocatorsData(apiResponse)) {
      return [];
    }

    return Object.entries(apiResponse).map(
      ([fieldName, locatorData], index) => {
        // Safely handle the data with defaults
        const primary = Array.isArray(locatorData?.primary)
          ? locatorData.primary
          : [];
        const secondary = Array.isArray(locatorData?.secondary)
          ? locatorData.secondary
          : [];

        return {
          id: index + 1,
          parameters: fieldName,
          primaryLocators: primary.join(", "),
          secondaryLocators: secondary.join(", "),
          originalData: {
            fieldName,
            primary,
            secondary,
          },
        };
      }
    );
  };

  const transformedData = transformApiResponseToTableData(locators);

  // Filter data based on search term
  const filteredData = transformedData.filter(
    (row) =>
      row.parameters.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.primaryLocators.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.secondaryLocators.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (row) => {
    console.log("Edit locator:", row);
    setEditingRow(row);
    setEditedData({
      fieldName: row.originalData.fieldName,
      primary: [...row.originalData.primary],
      secondary: [...row.originalData.secondary],
    });
    setEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setEditingRow(null);
    setEditedData({
      fieldName: "",
      primary: [],
      secondary: [],
    });
  };

  const handleSaveEdit = () => {
    // Create updated locators object
    const updatedLocators = { ...locators };

    // Remove old field name if it changed
    if (editingRow.originalData.fieldName !== editedData.fieldName) {
      delete updatedLocators[editingRow.originalData.fieldName];
    }

    // Add/update with new data
    updatedLocators[editedData.fieldName] = {
      primary: editedData.primary,
      secondary: editedData.secondary,
    };

    console.log("Updated locators:", updatedLocators);

    // Call parent update function
    onLocatorsUpdate(updatedLocators);

    handleCloseDialog();
  };

  const handlePrimaryLocatorChange = (index, value) => {
    const newPrimary = [...editedData.primary];
    newPrimary[index] = value;
    setEditedData({ ...editedData, primary: newPrimary });
  };

  const handleSecondaryLocatorChange = (index, value) => {
    const newSecondary = [...editedData.secondary];
    newSecondary[index] = value;
    setEditedData({ ...editedData, secondary: newSecondary });
  };

  const addPrimaryLocator = () => {
    setEditedData({
      ...editedData,
      primary: [...editedData.primary, ""],
    });
  };

  const addSecondaryLocator = () => {
    setEditedData({
      ...editedData,
      secondary: [...editedData.secondary, ""],
    });
  };

  const removePrimaryLocator = (index) => {
    const newPrimary = editedData.primary.filter((_, i) => i !== index);
    setEditedData({ ...editedData, primary: newPrimary });
  };

  const removeSecondaryLocator = (index) => {
    const newSecondary = editedData.secondary.filter((_, i) => i !== index);
    setEditedData({ ...editedData, secondary: newSecondary });
  };

  const columns = [
    {
      key: "parameters",
      label: "Parameters",
      sortable: true,
      maxWidth: "200px",
    },
    {
      key: "primaryLocators",
      label: "Primary Locators",
      sortable: true,
      maxWidth: "200px",
    },
    {
      key: "secondaryLocators",
      label: "Secondary Locators",
      sortable: true,
      maxWidth: "200px",
    },
  ];

  // Check if locators data is invalid
  const isInvalidData = !isValidLocatorsData(locators);
  const errorMessage =
    typeof locators === "string"
      ? locators
      : "Invalid locators data format. Please provide valid HTML source code or locators data.";

  return (
    <Container
      sx={{
        height: "100%",
        py: { xs: 2, md: 3 },
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          mb: { xs: 2, md: 3 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "20px", sm: "24px", md: "28px" },
            fontWeight: 700,
            color: "#1a1a1a",
            fontFamily: "Manrope, sans-serif",
            lineHeight: 1.2,
          }}
        >
          Locators
        </Typography>
      </Box>

      <Card
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          mb: 4,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Search Bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 600,
                color: "#333",
                fontSize: "18px",
              }}
            >
              Locators
            </Typography>

            <TextField
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearch}
              size="small"
              disabled={isInvalidData}
              sx={{
                width: "300px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Manrope",
                },
                "& .MuiInputBase-input": {
                  fontFamily: "Manrope",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#666" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Show error message if data is invalid */}
          {isInvalidData ? (
            <Box sx={{ p: 4 }}>
              <Alert
                severity="warning"
                sx={{
                  fontFamily: "Manrope",
                  fontSize: "14px",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  No Locators Available
                </Typography>
                <Typography variant="body2">{errorMessage}</Typography>
              </Alert>
            </Box>
          ) : (
            /* Table */
            <CustomTable
              data={filteredData}
              columns={columns}
              searchable={false}
              selectable={false}
              sortable={true}
              isEdit={true}
              onEdit={handleEdit}
              showTitle={false}
              showSearch={false}
              containerProps={{
                sx: { boxShadow: "none" },
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Manrope", fontWeight: 600 }}>
          Edit Locator
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Field Name */}
            <TextField
              label="Parameter Name"
              value={editedData.fieldName}
              onChange={(e) =>
                setEditedData({ ...editedData, fieldName: e.target.value })
              }
              fullWidth
              sx={{ fontFamily: "Manrope" }}
            />

            {/* Primary Locators */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Primary Locators
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addPrimaryLocator}
                  sx={{ textTransform: "none" }}
                >
                  Add
                </Button>
              </Box>
              {editedData.primary.map((locator, index) => (
                <Box key={index} sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <TextField
                    value={locator}
                    onChange={(e) =>
                      handlePrimaryLocatorChange(index, e.target.value)
                    }
                    fullWidth
                    size="small"
                    placeholder={`Primary locator ${index + 1}`}
                  />
                  <Button
                    size="small"
                    onClick={() => removePrimaryLocator(index)}
                    sx={{ minWidth: "auto", color: "error.main" }}
                  >
                    <CloseIcon />
                  </Button>
                </Box>
              ))}
            </Box>

            {/* Secondary Locators */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Secondary Locators
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addSecondaryLocator}
                  sx={{ textTransform: "none" }}
                >
                  Add
                </Button>
              </Box>
              {editedData.secondary.map((locator, index) => (
                <Box key={index} sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <TextField
                    value={locator}
                    onChange={(e) =>
                      handleSecondaryLocatorChange(index, e.target.value)
                    }
                    fullWidth
                    size="small"
                    placeholder={`Secondary locator ${index + 1}`}
                  />
                  <Button
                    size="small"
                    onClick={() => removeSecondaryLocator(index)}
                    sx={{ minWidth: "auto", color: "error.main" }}
                  >
                    <CloseIcon />
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#0094FB",
              "&:hover": { backgroundColor: "#0078D4" },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LocatorsPage;
